/**
 * FiltersDrawer Component
 *
 * Sidebar navigation and filters panel with updated design system styling.
 * Maintains all existing functionality with improved visual design.
 * Supports mobile slide-out drawer mode.
 */

import React, { useMemo } from 'react';
import {
    Box,
    Typography,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
    TextField,
    InputAdornment,
    alpha,
    ToggleButton,
    ToggleButtonGroup,
    Drawer,
} from '@mui/material';
import { Button } from '@components/design-system';
import { useSelector } from 'react-redux';

// Icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BusinessIcon from '@mui/icons-material/Business';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ComputerIcon from '@mui/icons-material/Computer';
import BadgeIcon from '@mui/icons-material/Badge';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

// Types and existing components
import type { RoleUtils } from '@/types/permissions';
import { OrgFilters } from '@components/OrgFilters';
import { Selection } from './orgNavTypes';
import type { FilterInput } from './OrgNav';
import type { SystemFilter } from '@/types/systems';
import type { SystemAssigned } from '@consts/systems';
import { SystemTableFilters } from './Tables/SystemTable/SystemTableFilters';

// Design system tokens
import { colors, typography as typographyTokens, radii } from '@components/design-system/tokens';

interface FiltersDrawerProps {
    /** Currently selected entity tab */
    selectedTab: Selection;
    /** Callback to change the selected tab */
    setSelectedTab: (tab: Selection) => void;
    /** Org-level filters */
    orgFilters: FilterInput;
    setOrgFilter: (k: keyof FilterInput, v: string, one?: boolean) => void;
    /** System-level filters */
    systemFilters: SystemFilter;
    setSystemFilter: (k: keyof SystemFilter, v: string, one?: boolean) => void;
    assigned: SystemAssigned;
    setAssigned: (v: SystemAssigned) => void;
    /** Search term for system table */
    searchTerm: string;
    setSearchTerm?: (v: string) => void;
    /** Location status filter */
    locationStatus?: 'active' | 'archived';
    setLocationStatus?: (v: 'active' | 'archived') => void;
    /** Clear helpers */
    onClearOrg: () => void;
    onClearSystem: () => void;
    /** Callback invoked when user clicks "+" to create new entity */
    onCreate?: (type: Selection) => void;
    /** Counts for nav items */
    counts?: {
        companies?: number;
        resellers?: number;
        locations?: number;
        employees?: number;
        systems?: number;
    };
    /** Mobile mode props */
    isMobile?: boolean;
    mobileOpen?: boolean;
    onMobileClose?: () => void;
}

// Nav item component with new styling
interface NavItemProps {
    icon: React.ReactNode;
    label: string;
    count?: number;
    selected: boolean;
    onClick: () => void;
    onAdd?: () => void;
    showAdd?: boolean;
}

const NavItem: React.FC<NavItemProps> = ({ icon, label, count, selected, onClick, onAdd, showAdd }) => (
    <Box
        onClick={onClick}
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1.5,
            py: 1,
            mb: 0.5,
            cursor: 'pointer',
            borderRadius: `${radii.md}px`,
            position: 'relative',
            transition: 'all 150ms ease',
            backgroundColor: selected ? colors.primary[50] : 'transparent',
            border: `1px solid ${selected ? colors.primary[200] : 'transparent'}`,
            '&:hover': {
                backgroundColor: selected ? colors.primary[50] : colors.neutral[50],
                border: `1px solid ${selected ? colors.primary[200] : colors.neutral[200]}`,
            },
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
            <Box
                sx={{
                    width: 32,
                    height: 32,
                    borderRadius: `${radii.md}px`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: selected ? colors.primary[100] : colors.neutral[100],
                    color: selected ? colors.primary[600] : colors.neutral[500],
                    transition: 'all 150ms ease',
                    '& svg': { fontSize: 18 },
                }}>
                {icon}
            </Box>
            <Typography
                sx={{
                    fontWeight: typographyTokens.fontWeight.medium,
                    fontSize: typographyTokens.fontSize.sm,
                    color: selected ? colors.primary[700] : colors.neutral[700],
                    flex: 1,
                }}>
                {label}
            </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {count !== undefined && (
                <Typography
                    sx={{
                        backgroundColor: selected ? colors.primary[100] : colors.neutral[100],
                        color: selected ? colors.primary[700] : colors.neutral[600],
                        px: 1,
                        py: 0.25,
                        borderRadius: `${radii.full}px`,
                        fontSize: typographyTokens.fontSize.xs,
                        fontWeight: typographyTokens.fontWeight.semibold,
                        minWidth: 24,
                        textAlign: 'center',
                        lineHeight: 1.5,
                    }}>
                    {count}
                </Typography>
            )}
            {showAdd && onAdd && (
                <IconButton
                    size='small'
                    sx={{ 
                        p: 0.5,
                        borderRadius: `${radii.md}px`,
                        color: colors.neutral[400],
                        '&:hover': {
                            backgroundColor: colors.neutral[100],
                            color: colors.primary[500],
                        },
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        onAdd();
                    }}>
                    <AddIcon sx={{ fontSize: 18 }} />
                </IconButton>
            )}
        </Box>
    </Box>
);

// Section header component
const SectionHeader: React.FC<{ children: React.ReactNode; sx?: object }> = ({ children, sx }) => (
    <Typography
        sx={{
            fontSize: typographyTokens.fontSize.xs,
            fontWeight: typographyTokens.fontWeight.semibold,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: colors.neutral[400],
            mb: 1.5,
            px: 0.5,
            ...sx,
        }}>
        {children}
    </Typography>
);

// Styled accordion
const StyledAccordion: React.FC<{
    title: string;
    defaultExpanded?: boolean;
    children: React.ReactNode;
}> = ({ title, defaultExpanded = true, children }) => (
    <Accordion
        defaultExpanded={defaultExpanded}
        elevation={0}
        disableGutters
        sx={{
            backgroundColor: 'transparent',
            '&:before': { display: 'none' },
            '&.Mui-expanded': { margin: 0 },
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden',
        }}>
        <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: colors.neutral[500] }} />}
            sx={{
                px: 0,
                minHeight: 40,
                '& .MuiAccordionSummary-content': { my: 1 },
            }}>
            <SectionHeader sx={{ mb: 0 }}>{title}</SectionHeader>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0, pt: 0, overflow: 'hidden', width: '100%', maxWidth: '100%' }}>
            {children}
        </AccordionDetails>
    </Accordion>
);

export const FiltersDrawer: React.FC<FiltersDrawerProps> = ({
    selectedTab,
    setSelectedTab,
    orgFilters,
    setOrgFilter,
    systemFilters,
    setSystemFilter,
    assigned,
    setAssigned,
    searchTerm,
    setSearchTerm,
    locationStatus,
    setLocationStatus,
    onClearOrg,
    onClearSystem,
    onCreate,
    counts = {},
    isMobile = false,
    mobileOpen = false,
    onMobileClose,
}) => {
    // Current user's role utilities for permission checks
    const role = useSelector((state: any) => state.auth.Roles.role as RoleUtils);

    // Check if any filters are active
    const hasActiveFilters = useMemo(() => {
        const hasOrgFilters =
            Object.values(orgFilters.companies).some(Boolean) ||
            Object.values(orgFilters.resellers).some(Boolean) ||
            Object.values(orgFilters.locations).some(Boolean);
        
        const hasSystemFilters =
            Object.values(systemFilters.model).some(Boolean) ||
            Object.values(systemFilters.status).some(Boolean);
        
        const hasSearchTerm = Boolean(searchTerm);
        const hasLocationStatusChange = locationStatus === 'archived';
        
        return hasOrgFilters || hasSystemFilters || hasSearchTerm || hasLocationStatusChange;
    }, [orgFilters, systemFilters, searchTerm, locationStatus]);

    // Clear all filters
    const handleClearAll = () => {
        onClearOrg();
        onClearSystem();
        setSearchTerm?.('');
        setLocationStatus?.('active');
    };

    const canCreateCompany = role.isAdmin && role.stores.edit;
    const canCreateReseller = role.stores.edit && role.resellers.fullAccess && role.locations.fullAccess;
    const canCreateLocation = role.stores.edit && role.locations.fullAccess;

    const width = 280;

    // Shared drawer content
    const drawerContent = (
        <Box
            sx={{
                width,
                maxWidth: width,
                minWidth: width,
                flexShrink: 0,
                borderRight: isMobile ? 'none' : `1px solid ${colors.neutral[200]}`,
                height: '100%',
                overflowY: 'auto',
                overflowX: 'hidden',
                backgroundColor: colors.neutral[0],
                display: 'flex',
                flexDirection: 'column',
            }}>
            {/* Mobile Header */}
            {isMobile && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2,
                        py: 1.5,
                        borderBottom: `1px solid ${colors.neutral[200]}`,
                    }}>
                    <Typography
                        sx={{
                            fontSize: typographyTokens.fontSize.lg,
                            fontWeight: typographyTokens.fontWeight.semibold,
                            color: colors.neutral[900],
                        }}>
                        Navigation & Filters
                    </Typography>
                    <IconButton
                        onClick={onMobileClose}
                        size='small'
                        sx={{ color: colors.neutral[500] }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            )}

            {/* Organization Navigation */}
            <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                <SectionHeader>Organization</SectionHeader>

                <NavItem
                    icon={<BusinessIcon />}
                    label='Companies'
                    count={counts.companies}
                    selected={selectedTab === Selection.Company}
                    onClick={() => {
                        setSelectedTab(Selection.Company);
                        if (isMobile) onMobileClose?.();
                    }}
                    onAdd={() => onCreate?.(Selection.Company)}
                    showAdd={canCreateCompany}
                />
                <NavItem
                    icon={<StorefrontIcon />}
                    label='Resellers'
                    count={counts.resellers}
                    selected={selectedTab === Selection.Reseller}
                    onClick={() => {
                        setSelectedTab(Selection.Reseller);
                        if (isMobile) onMobileClose?.();
                    }}
                    onAdd={() => onCreate?.(Selection.Reseller)}
                    showAdd={canCreateReseller}
                />
                <NavItem
                    icon={<LocationOnIcon />}
                    label='Locations'
                    count={counts.locations}
                    selected={selectedTab === Selection.Location}
                    onClick={() => {
                        setSelectedTab(Selection.Location);
                        if (isMobile) onMobileClose?.();
                    }}
                    onAdd={() => onCreate?.(Selection.Location)}
                    showAdd={canCreateLocation}
                />
                <NavItem
                    icon={<BadgeIcon />}
                    label='Employees'
                    count={counts.employees}
                    selected={selectedTab === Selection.Employee}
                    onClick={() => {
                        setSelectedTab(Selection.Employee);
                        if (isMobile) onMobileClose?.();
                    }}
                    onAdd={() => onCreate?.(Selection.Employee)}
                    showAdd={true}
                />
                <NavItem
                    icon={<ComputerIcon />}
                    label='Systems'
                    count={counts.systems}
                    selected={selectedTab === Selection.System}
                    onClick={() => {
                        setSelectedTab(Selection.System);
                        if (isMobile) onMobileClose?.();
                    }}
                />
            </Box>

            <Divider sx={{ mx: 2, borderColor: colors.neutral[200] }} />

            {/* Organization Filters */}
            <Box sx={{ px: 2, pb: 2, overflowX: 'hidden', maxWidth: '100%', boxSizing: 'border-box', flex: 1 }}>
                <StyledAccordion title='Filter by Organization'>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            width: '100%',
                            maxWidth: '100%',
                            overflow: 'hidden',
                        }}>
                        <OrgFilters
                            orgFilters={orgFilters}
                            setOrgFilters={setOrgFilter as any}
                            handleClear={onClearOrg}
                            showLocations={
                                selectedTab === Selection.Location ||
                                selectedTab === Selection.System ||
                                selectedTab === Selection.Employee
                            }
                            showResellers={selectedTab !== Selection.Company}
                        />

                        {/* Archive Status Toggle */}
                        {(selectedTab === Selection.Location ||
                            selectedTab === Selection.Company ||
                            selectedTab === Selection.Reseller) &&
                            setLocationStatus && (
                            <Box sx={{ mt: 1.5 }}>
                                <Typography
                                    component='label'
                                    sx={{
                                        display: 'block',
                                        fontSize: typographyTokens.fontSize.sm,
                                        fontWeight: typographyTokens.fontWeight.medium,
                                        color: colors.neutral[500],
                                        mb: '6px',
                                    }}>
                                    Status
                                </Typography>
                                <ToggleButtonGroup
                                    value={locationStatus}
                                    exclusive
                                    onChange={(_, value) => {
                                        if (value !== null) {
                                            setLocationStatus(value);
                                        }
                                    }}
                                    size='small'
                                    sx={{
                                        width: '100%',
                                        backgroundColor: colors.neutral[100],
                                        borderRadius: `${radii.md}px`,
                                        '& .MuiToggleButton-root': {
                                            flex: 1,
                                            border: 'none',
                                            borderRadius: `${radii.md}px !important`,
                                            textTransform: 'none',
                                            fontSize: typographyTokens.fontSize.sm,
                                            fontWeight: 500,
                                            color: colors.neutral[600],
                                            py: 0.75,
                                            '&:hover': {
                                                backgroundColor: colors.neutral[200],
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: colors.primary[500],
                                                color: colors.neutral[0],
                                                '&:hover': {
                                                    backgroundColor: colors.primary[600],
                                                },
                                            },
                                        },
                                    }}>
                                    <ToggleButton value='active'>Active</ToggleButton>
                                    <ToggleButton value='archived'>Archived</ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        )}
                    </Box>
                </StyledAccordion>

                {/* System Filters */}
                {selectedTab === Selection.System && (
                    <StyledAccordion title='Systems'>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {/* Search Systems */}
                            <TextField
                                fullWidth
                                size='small'
                                placeholder='Search systems...'
                                value={searchTerm}
                                onChange={(e) => setSearchTerm?.(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position='start'>
                                            <SearchIcon sx={{ fontSize: 20, color: colors.neutral[400] }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        fontSize: typographyTokens.fontSize.sm,
                                        backgroundColor: colors.neutral[0],
                                        borderRadius: `${radii.md}px`,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: colors.neutral[300],
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: colors.neutral[400],
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: colors.primary[500],
                                        },
                                    },
                                }}
                            />

                            {/* System Table Filters (Model, Status) */}
                            <SystemTableFilters
                                filters={systemFilters}
                                setFilter={setSystemFilter as any}
                                assigned={assigned}
                                setAssigned={setAssigned}
                                searchTerm={searchTerm}
                            />
                        </Box>
                    </StyledAccordion>
                )}

                {/* Employee Filters */}
                {selectedTab === Selection.Employee && (
                    <StyledAccordion title='Employees'>
                        <TextField
                            fullWidth
                            size='small'
                            placeholder='Search employees...'
                            value={searchTerm}
                            onChange={(e) => setSearchTerm?.(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <SearchIcon sx={{ fontSize: 20, color: colors.neutral[400] }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontSize: typographyTokens.fontSize.sm,
                                    backgroundColor: colors.neutral[0],
                                    borderRadius: `${radii.md}px`,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: colors.neutral[300],
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: colors.neutral[400],
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: colors.primary[500],
                                    },
                                },
                            }}
                        />
                    </StyledAccordion>
                )}

                {/* Clear All Filters Button - Always at bottom */}
                <Divider sx={{ my: 2, borderColor: colors.neutral[200] }} />
                <Button
                    variant='secondary'
                    fullWidth
                    onClick={handleClearAll}
                    disabled={!hasActiveFilters}>
                    Reset
                </Button>
            </Box>
        </Box>
    );

    // Mobile: render in a sliding drawer from the right
    if (isMobile) {
        return (
            <Drawer
                anchor='right'
                open={mobileOpen}
                onClose={onMobileClose}
                PaperProps={{
                    sx: {
                        width,
                        maxWidth: '85vw',
                    },
                }}
                ModalProps={{
                    keepMounted: true, // Better mobile performance
                }}>
                {drawerContent}
            </Drawer>
        );
    }

    // Desktop: render inline
    return drawerContent;
};
